import { Label } from "@/components/ui/label";

export const AUDIO_FREQS = [125, 250, 500, 1000, 2000, 4000, 8000];
const SERIES = [
  { k: "dx", label: "Dx via aerea", color: "#dc2626", sym: "O" },
  { k: "sx", label: "Sx via aerea", color: "#2563eb", sym: "X" },
  { k: "dx_bc", label: "Dx via ossea", color: "#dc2626", sym: "<", dashed: true },
  { k: "sx_bc", label: "Sx via ossea", color: "#2563eb", sym: ">", dashed: true },
];

const num = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
export const audiogramPTA = (row) => {
  const vals = [500, 1000, 2000, 4000].map((f) => num(row?.[f])).filter((v) => v !== null && !isNaN(v));
  return vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
};
export const audiogramHasData = (a) => SERIES.some((s) => AUDIO_FREQS.some((f) => num(a?.[s.k]?.[f]) !== null));
export const serializeAudiogram = (a) => {
  if (!audiogramHasData(a)) return "";
  const parts = SERIES.filter((s) => AUDIO_FREQS.some((f) => num(a?.[s.k]?.[f]) !== null)).map((s) => {
    const pts = AUDIO_FREQS.filter((f) => num(a[s.k]?.[f]) !== null).map((f) => `${f >= 1000 ? f / 1000 + " kHz" : f + " Hz"} ${a[s.k][f]} dB`).join(", ");
    const pta = audiogramPTA(a[s.k]);
    return `${s.label}: ${pts}${pta !== null ? ` (PTA ${pta} dB HL)` : ""}`;
  });
  return "Audiogramma tonale — " + parts.join("; ");
};

const W = 420, H = 260, PL = 40, PR = 16, PT = 14, PB = 28;
const xOf = (f) => PL + (AUDIO_FREQS.indexOf(f) / (AUDIO_FREQS.length - 1)) * (W - PL - PR);
const yOf = (db) => PT + ((Math.min(120, Math.max(-10, db)) + 10) / 130) * (H - PT - PB);

export const AudiogramChart = ({ value, previous }) => (
  <svg viewBox={`0 0 ${W} ${H}`} className="w-full max-w-[460px] bg-white rounded-md border border-slate-200" data-testid="audiogram-chart">
    <rect x={PL} y={yOf(-10)} width={W - PL - PR} height={yOf(25) - yOf(-10)} fill="#ecfdf5" />
    {[-10, 0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120].map((db) => (
      <g key={db}>
        <line x1={PL} x2={W - PR} y1={yOf(db)} y2={yOf(db)} stroke={db === 0 ? "#64748b" : "#e2e8f0"} strokeWidth={db === 0 ? 1 : 0.7} />
        {db % 20 === 0 && <text x={PL - 6} y={yOf(db) + 3} fontSize="8" textAnchor="end" fill="#64748b">{db}</text>}
      </g>
    ))}
    {AUDIO_FREQS.map((f) => (
      <g key={f}>
        <line x1={xOf(f)} x2={xOf(f)} y1={PT} y2={H - PB} stroke="#e2e8f0" strokeWidth="0.7" />
        <text x={xOf(f)} y={H - PB + 12} fontSize="8" textAnchor="middle" fill="#64748b">{f >= 1000 ? f / 1000 + "k" : f}</text>
      </g>
    ))}
    <text x={PL - 30} y={H / 2} fontSize="7.5" fill="#64748b" transform={`rotate(-90 ${PL - 30} ${H / 2})`} textAnchor="middle">dB HL</text>
    <text x={(PL + W - PR) / 2} y={H - 4} fontSize="7.5" fill="#64748b" textAnchor="middle">Frequenza (Hz)</text>
    {previous && SERIES.slice(0, 2).map((s) => {
      const pts = AUDIO_FREQS.filter((f) => num(previous?.[s.k]?.[f]) !== null).map((f) => [xOf(f), yOf(num(previous[s.k][f]))]);
      if (!pts.length) return null;
      return (
        <g key={`prev-${s.k}`} data-testid={`audiogram-prev-${s.k}`} opacity="0.75">
          <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke="#94a3b8" strokeWidth="1.2" strokeDasharray="3 3" />
          {pts.map(([px, py], i) => <text key={i} x={px} y={py + 3.5} fontSize="9" fontWeight="600" textAnchor="middle" fill="#94a3b8">{s.sym === "X" ? "x" : "o"}</text>)}
        </g>
      );
    })}
    {SERIES.map((s) => {
      const pts = AUDIO_FREQS.filter((f) => num(value?.[s.k]?.[f]) !== null).map((f) => [xOf(f), yOf(num(value[s.k][f]))]);
      if (!pts.length) return null;
      return (
        <g key={s.k}>
          <polyline points={pts.map((p) => p.join(",")).join(" ")} fill="none" stroke={s.color} strokeWidth="1.4" strokeDasharray={s.dashed ? "4 3" : undefined} />
          {pts.map(([px, py], i) => <text key={i} x={px} y={py + 4} fontSize="11" fontWeight="700" textAnchor="middle" fill={s.color}>{s.sym}</text>)}
        </g>
      );
    })}
  </svg>
);

export const AudiogramEditor = ({ value, onChange, previous, compare, onToggleCompare }) => {
  const [showBC, setShowBC] = [!!value?.show_bc, (v) => onChange({ ...(value || {}), show_bc: v })];
  const set = (k, f, v) => onChange({ ...(value || {}), [k]: { ...((value || {})[k] || {}), [f]: v } });
  const rows = SERIES.filter((s) => !s.dashed || showBC);
  const prevAg = compare ? previous?.audiogram : null;
  const delta = (k) => { const a = audiogramPTA(value?.[k]), b = audiogramPTA(prevAg?.[k]); if (a === null || b === null) return null; return a - b; };
  return (
    <div className="rounded-lg border border-violet-200 bg-white p-3 space-y-3" data-testid="audiogram-editor">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-xs font-bold uppercase tracking-wide text-violet-800">Audiogramma tonale</div>
        <div className="flex items-center gap-3 flex-wrap">
          {previous && (
            <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
              <input type="checkbox" checked={!!compare} onChange={(e) => onToggleCompare?.(e.target.checked)} data-testid="audiogram-compare" />
              Confronta con visita del {new Date(previous.created_at).toLocaleDateString("it-IT")}
            </label>
          )}
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600 cursor-pointer">
            <input type="checkbox" checked={showBC} onChange={(e) => setShowBC(e.target.checked)} data-testid="audiogram-show-bc" /> Aggiungi via ossea
          </label>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs w-full min-w-[520px]">
          <thead>
            <tr className="text-slate-500">
              <th className="text-left font-medium pb-1 pr-2">dB HL</th>
              {AUDIO_FREQS.map((f) => <th key={f} className="font-medium pb-1 text-center">{f >= 1000 ? f / 1000 + " kHz" : f + " Hz"}</th>)}
              <th className="font-medium pb-1 text-center">PTA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.k}>
                <td className="pr-2 py-0.5 font-semibold whitespace-nowrap" style={{ color: s.color }}>{s.sym} {s.label}</td>
                {AUDIO_FREQS.map((f) => (
                  <td key={f} className="px-0.5 py-0.5">
                    <input type="number" step="5" min="-10" max="120" value={value?.[s.k]?.[f] ?? ""} onChange={(e) => set(s.k, f, e.target.value)}
                           data-testid={`audiogram-${s.k}-${f}`}
                           className="h-8 w-14 rounded border border-slate-300 text-center text-xs focus:border-violet-500 outline-none" />
                  </td>
                ))}
                <td className="text-center font-semibold text-slate-700" data-testid={`audiogram-pta-${s.k}`}>
                  {audiogramPTA(value?.[s.k]) ?? "—"}
                  {prevAg && delta(s.k) !== null && (
                    <span className={`ml-1 text-[10px] ${delta(s.k) > 0 ? "text-red-600" : delta(s.k) < 0 ? "text-emerald-600" : "text-slate-400"}`} data-testid={`audiogram-delta-${s.k}`}>
                      ({delta(s.k) > 0 ? "+" : ""}{delta(s.k) === 0 ? "=" : delta(s.k)})
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(audiogramHasData(value) || (prevAg && audiogramHasData(prevAg))) && <AudiogramChart value={value} previous={prevAg} />}
      <Label className="text-[11px] text-slate-500 font-normal">O = dx via aerea (rosso) · X = sx via aerea (blu) · &lt; &gt; via ossea{prevAg ? " · grigio tratteggiato = visita precedente" : ""} · PTA = media 500-1000-2000-4000 Hz</Label>
    </div>
  );
};
